import React, { useMemo } from "react";
import styles from "./styles.module.css";
import clsx from "clsx";
import ImageComponent from "../../ImageComponent";
import KukariScore from "../KukariScore";
import KukariBounceAnimation from "../KukariBounceAnimation";
import { useSelector } from "react-redux";
import { getAllKukariPositions } from "../../../store/selectors/ludoboardSelectors";
import { _entries, _get, _includes, _isNil, _map, _noop, _reduce, _slice } from "../../../utils/helpers/zupeeLodash";
import { boxStyle, kukariStyle } from "./style";
import { ICoordinates, PLAYER_NUMBER } from "../../../pages/GamePlay/types";
import { getHeight, getWidth } from "../../../utils/relativeSizes";
import { getEventProcessor } from "../../../pages/GamePlay/eventProcessor";
import Lottie from "react-lottie";
import squareRipple from "../../../assets/lottie/token_square_ripple.json";
import { boxNumberToShowHandAnimation } from "../utils";
import KukriHandAnimationLudoNx from "../KukriHandAnimationLudoNx";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const OneBox: React.FC<{
  kukariImage: string;
  boxLength: number;
  index: number;
  isInverted: boolean;
  handleOtherPlayerTokenClick: () => void;
  kukari: { i: number; isMovable: boolean; playerNumber: number; score?: number };
  testID?: string;
}> = ({
  kukariImage,
  boxLength,
  index,
  isInverted,
  handleOtherPlayerTokenClick,
  kukari,
  testID,
}) => {

    const styleSuffix = useMemo(() => {
      if (boxLength === 3) {
        return isInverted ? "_b_t" : "_t_b";
      }
      return "";
    }, [boxLength, isInverted]);

    const { isMovable, i: tokenIndex } = kukari;

    return (
      <div className={clsx(`${styles.kukariWrapper} kukari_${boxLength}_${index}`)}
        style={{
          transform: kukariStyle[`kukari_${boxLength}_${index}` as keyof typeof kukariStyle]?.transform
            .map(t => {
              if ('translateX' in t) return `translateX(${t.translateX}px)`;
              if ('translateY' in t) return `translateY(${t.translateY}px)`;
              if ('scaleX' in t) return `scaleX(${t.scaleX})`;
              if ('scaleY' in t) return `scaleY(${t.scaleY})`;
              return '';
            })
            .join(' ')
        }}
      >
        {isMovable ? (
          <KukariBounceAnimation
            kukariImage={kukariImage}
            tokenIndex={tokenIndex}
            testID={testID}
            onMoveTokenClick={(index) => console.log("Token clicked:", index)}
          />
        ) : (
          <div
            className={clsx(styles.kukariImgWrapper)}
            onClick={(e) => {
              // handleOtherPlayerTokenClick(tokenIndex)
              handleOtherPlayerTokenClick()
              // console.log("hello", handleOtherPlayerTokenClick)
            }}
          >
            <ImageComponent
              src={kukariImage}
              alt="kukari img"
              className={clsx(boxLength > 1 ? styles.kukariScaledDown : styles.kukariBase)}
              style={{
                width: getWidth(19),
                height: getHeight(32)
              }}
            />
            <KukariScore kukari={kukari} />
          </div>
        )}
      </div>
    );
  };

const Boxes: React.FC = () => {
  const {t} = useTranslation();
  const COMMON_BOXES_STATIC = {
    4: [
      {
        i: 0,
        isMovable: false,
        playerNumber: 2,
        score: 34,
      },
      {
        i: 1,
        isMovable: false,
        playerNumber: 2,
        score: 50,
      }
    ],
  };

  const { commonBoxes, boardConfig, homeBoxes, finalBoxes, coloredBoxes } = useSelector(getAllKukariPositions);

  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: squareRipple,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const posToShowHandAnimation = boxNumberToShowHandAnimation(boardConfig, commonBoxes, 44)

  return (
    <>
      <div className={styles.boxesContainer}>
        {/* common boxes */}
        {_map(_entries(commonBoxes), ([pos, box]) => {
          const boxPosition = Number(pos);
          const { translateX, translateY, zIndex } = boardConfig.COMMON_BOXES[Number(pos)]
          const boxLength = box.length > 6 ? 6 : box.length
          const boxToRender = box.length > 6 ? _slice(box, box.length - boxLength) : box
          // console.log({boxToRender, pos}, 'boxToRender')
          return (
            <div
              key={`box-${pos}`}
              className={clsx(styles.box, "absolute")}
              style={{
                ...boxStyle,
                transform: `translate(${translateX}px, ${translateY}px)`,
                zIndex: zIndex,
              }}
            >
              {_map(boxToRender, (kukari, index) => (
                <OneBox
                  key={`kukari-${kukari.playerNumber}-${kukari.i}`}
                  boxLength={boxLength}
                  index={index}
                  kukariImage={boardConfig.KUKARI_IMAGE[kukari.playerNumber] as string}
                  isInverted={boxPosition < 24 || boxPosition > 49}
                  handleOtherPlayerTokenClick={
                    getEventProcessor()?.handleOtherPlayerTokenClick?.bind(null, kukari.playerNumber!, kukari.score!) || _noop
                  }
                  kukari={kukari}
                  testID={`${kukari.playerNumber}Player${kukari.i}Button`}
                />))
              }
              {Number(pos) === posToShowHandAnimation && (
                <div className={styles.kukriHandAnimation}
                  style={{
                    left: getWidth(15),
                    top: getWidth(15),
                  }}
                >
                  <KukriHandAnimationLudoNx />
                </div>
              )}
            </div>
          )
        })}

        {/* home boxes */}
        {_map(_entries(homeBoxes), ([playerNumber, boxes]) => {
          return _map(_entries(boxes), ([pos, box]) => {
            const { translateX, translateY, zIndex } = boardConfig.HOME_BOXES[playerNumber as unknown as PLAYER_NUMBER]?.[
              Number(pos)
            ] as ICoordinates
            const showRipple = _reduce(box, (acc, item) => acc || item?.isMovable, false)

            function handleOtherPlayerTokenClick(): void {
              // toast.error(t("notClickable"));
              console.log("notClickable");
            }

            return (
              <div
                key={`hb-${playerNumber}-${pos}`}
                style={{
                  transform: `translate(${translateX}px, ${translateY}px)`,
                  zIndex: zIndex,
                }}
              >
                {showRipple && (
                  <Lottie
                    style={{
                      position: 'absolute',
                      width: getWidth(29),
                      height: getWidth(29),
                      left: getWidth(-15),
                      top: getWidth(-15)
                    }}
                    options={defaultOptions}
                    eventListeners={[
                      {
                        eventName: 'complete',
                        callback: () => {
                          console.log("showRipple animation completed")
                        },
                      },
                    ]}
                  />
                )}
                {_map(box, (kukari, index) => (
                  <OneBox
                    key={`hb-${playerNumber}-${pos}-${index}`}
                    kukari={{ ...kukari, playerNumber: Number(playerNumber) }}
                    kukariImage={boardConfig.KUKARI_IMAGE[Number(playerNumber) as keyof typeof boardConfig.KUKARI_IMAGE] as string}
                    boxLength={box.length}
                    testID={`${playerNumber}Player${kukari.i}Button`}
                    handleOtherPlayerTokenClick={handleOtherPlayerTokenClick}
                    index={index}
                    isInverted={Number(playerNumber) === PLAYER_NUMBER.ONE || Number(playerNumber) === PLAYER_NUMBER.TWO}
                  />
                ))}
              </div>
            )
          })
        })}

        {/* final boxes */}
        {_map(_entries(finalBoxes), ([playerNumber, tokenIndices]) => {
          return (
            <>
              {_map(tokenIndices, tokenIndex => {
                const { translateX, translateY } = boardConfig.FINAL_BOXES[playerNumber as unknown as PLAYER_NUMBER]?.[
                  tokenIndex
                ] as ICoordinates

                return (
                  <ImageComponent
                    src={boardConfig.DESTINY_IMAGE[playerNumber as unknown as PLAYER_NUMBER] || ""}
                    alt="destiny image"
                    style={{
                      width: getWidth(7),
                      height: getHeight(7),
                      position: 'absolute',
                      transform: `translateX(${translateX}px) translateY(${translateY}px)`
                    }}
                    key={`fb-${playerNumber}-${tokenIndex}`}
                  />
                )
              })}
            </>
          )
        })}
      </div>
    </>
  );
};

export default Boxes;
